// Nav.js

import React, { useState, useEffect } from "react";
import ApiRequest from "../ApiRequest";
import {
  StyledButton,
  StyledBox,
  StyledTypo,
  StyledPaper,
  StyledCarousel,
} from "../StyledMui";
import {
  TextField,
  TablePagination,
  Radio,
  Paper,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Divider,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import useStore from "../Store";
import { useAlert } from "../AlertProvider";
import SearchIcon from "@mui/icons-material/Search";

function Nav(props) {
  // Local state
  const [type, setType] = useState("friendtalk");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Zustand store states and actions
  const {
    getTemplates,
    templates,
    selectedTemplate,
    setSelectedTemplate,
    msg_img_link_url,
    setMsgImgLinkUrl,
    selectedChannel,
    setSelectedChannel,
    selectedChannelName,
    selectedPhone,
    setSelectedPhone,
    setSelectedChannelName,
    org,
    isMMS,
    setIsMMS,
    isAd,
    setIsAd,
    onLoading,
    offLoading,
  } = useStore((state) => state.header);

  const { initData, getTemplate, getChannel, noContentFiles, setNoContentFiles } = useStore(
    (state) => state.mapper
  );

  const {
    setIsVar,
    setVariableSwitchFlag,
    setPhoneMessageSwitchFlag,
  } = useStore((state) => state.preview);

  // Alert hook
  const { showAlert } = useAlert();

  // Hardcoded channels
  const [channels] = useState([
    { channelName: "DBU", channelPhone: "123-456-7890" },
    { channelName: "RBU", channelPhone: "234-567-8901" },
    { channelName: "OBU", channelPhone: "345-678-9012" },
  ]);

  // Effect to fetch templates when selectedChannelName changes
  useEffect(() => {
    if (selectedChannelName) {
      const fetchTemplates = async () => {
        setIsLoading(true);
        onLoading.call(this);
        await getTemplates(selectedChannelName);
        offLoading.call(this);
        setIsLoading(false);
      };
      fetchTemplates();
    }

  }, [selectedChannelName, getTemplates]);

  // Effect to handle initData changes
  useEffect(() => {
    if (initData && Object.keys(initData).length > 0) {
      console.log("Init Data:", initData);

      if (initData.templateId) {
        getTemplate(initData.templateId);
      }

      if (initData.channelCode) {
        getChannel(initData.channelCode);
      }

      if (initData.ismms) {
        setIsMMS(initData.ismms);
      }

      if (initData.adFlag) {
        setIsAd(initData.adFlag);
      }

      if (initData.templateType) {
        setType(initData.templateType);
      }

      if (initData.validationError) {
        showAlert(initData.validationError, "error", 0);
      }
    }
  }, [initData, getTemplate, getChannel, setIsMMS, setIsAd, showAlert]);

  // Effect to handle selectedTemplate changes
  useEffect(() => {
    setIsVar(false);
    setVariableSwitchFlag(false);
    setPhoneMessageSwitchFlag(false);

    if (
      selectedTemplate.templateMessageType === "FC" ||
      selectedTemplate.templateMessageType === "FL"
    ) {
      setIsAd(true);
    } else {
      setIsAd(false);
    }
  }, [
    selectedTemplate,
    setIsVar,
    setVariableSwitchFlag,
    setPhoneMessageSwitchFlag,
    setIsAd,
  ]);

  // Filtered templates based on searchKeyword
  const filteredTemplates = templates.filter((template) =>
    template.msg_no.toString().toLowerCase().includes(searchKeyword.toLowerCase()) ||
    template.msg_desc.toString().includes(searchKeyword.toLowerCase())
  );

  useEffect(() => {
    if (!selectedTemplate || !selectedTemplate.msg_no) {
      // 템플릿이 선택 안됐으면 초기화
      setMsgImgLinkUrl("");
      return;
    }
  
    async function fetchThumb() {
      setMsgImgLinkUrl(selectedTemplate.msg_img_link_url);
    }
  
    fetchThumb();
  }, [selectedTemplate, setMsgImgLinkUrl]);
  

  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);

  const currentTemplates = filteredTemplates.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Handler for page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Handler for items per page change
  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset current page when changing items per page
  };

  // Handler for search keyword change
  const handleSearchKeywordChange = (event) => {
    setSearchKeyword(event.target.value);
  };

  // Handler for channel selection change
  const handleChannelChange = (event) => {
    const channelName = event.target.value;
    console.log("Selected channelName:", channelName);
    setSelectedChannelName(channelName);

    const selectedChannelObject = channels.find(
      (channel) => channel.channelName === channelName
    );
    console.log("Selected Channel Object:", selectedChannelObject);
    if (selectedChannelObject) {
      setSelectedChannel(selectedChannelObject);
      setSelectedPhone(selectedChannelObject.channelPhone);
    }
    setCurrentPage(0);
  };

  // Handler for type toggle button
  const handleClickType = (event) => {
    setType(event.target.value);
    setCurrentPage(0);
  };

  // Determine the host URL based on selectedChannelName
  let host;
  switch (selectedChannelName) {
    case "OBU":
      host = "https://novo-obu.s3.ap-northeast-2.amazonaws.com/";
      break;
    case "RBU":
      host = "https://novo-rbu.s3.ap-northeast-2.amazonaws.com/";
      break;
    case "DBU":
      host = "https://novo-dbu.s3.ap-northeast-2.amazonaws.com/";
      break;
    default:
      host = "";
  }

  // Render function for No Content Files as TableRows
  const renderNoContentFiles = () => {
    if (!noContentFiles || noContentFiles.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={selectedTemplate && selectedTemplate.templateMessageType === "FC" ? 3 : 2}
            align="center"
          >
            {selectedTemplate ? "이용 가능한 콘텐츠가 없습니다." : "콘텐츠를 먼저 선택해주세요"}
          </TableCell>
        </TableRow>
      );
    }

    return noContentFiles.map((file, index) => (
      <TableRow key={index}>
        {selectedTemplate && selectedTemplate.templateMessageType === "FC" && (
          <TableCell>{index + 1}</TableCell>
        )}
        <TableCell>{file.btn_name}</TableCell>
        <TableCell>{file.btn_url}</TableCell>
      </TableRow>
    ));
  };

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
    handleThunNailUrlChange(template.msg_img_link_url);
  };

  const handleThunNailUrlChange = async (msg_img_link_url) => {
    if(!msg_img_link_url) {
      setMsgImgLinkUrl("https://www.novonordisk.com/");
    }
    else{
      setMsgImgLinkUrl(msg_img_link_url);
    }
  };


  return (
    <StyledPaper style={{ height: "100%" }}>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* Channel Selection Section */}
          <StyledBox>
            <StyledTypo>채널 선택</StyledTypo>
          </StyledBox>
          <Stack direction="column">
            <FormControl
              variant="standard"
              sx={{ width: "100%", margin: "5px", bgcolor: "white" }}
            >
              <InputLabel sx={{ transform: "none" }} id="c-name">
                채널명
              </InputLabel>
              <Select
                labelId="c-name"
                id="c-name"
                label="채널명"
                value={selectedChannelName}
                onChange={handleChannelChange}
                sx={{ height: 50 }}
              >
                {channels.map((channel, index) => (
                  <MenuItem
                    key={index}
                    value={channel.channelName}
                    name={index}
                  >
                    {channel.channelName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Divider />
          <br />

          {/* Content Loading Section */}
          <StyledBox>
            <StyledTypo>콘텐츠 불러오기</StyledTypo>
            <ToggleButtonGroup
              color="primary"
              exclusive
              onChange={handleClickType}
              value={type}
              sx={{
                alignSelf: "flex-end",
                height: "32px",
                overflow: "hidden",
              }}
            >
              <ToggleButton value="friendtalk">친구톡</ToggleButton>
              {/* 필요한 경우 다른 토글 버튼 추가 */}
            </ToggleButtonGroup>
          </StyledBox>

          {/* Search Bar */}
          <Stack direction="row" alignItems="center">
            <OutlinedInput
              sx={{ flexGrow: 1, height: "38px" }}
              placeholder="검색"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              onChange={handleSearchKeywordChange}
            />
          </Stack>

          {/* Templates Table */}
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%" }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>선택</TableCell>
                  <TableCell>콘텐츠 코드</TableCell>
                  <TableCell>콘텐츠 제목</TableCell>
                  <TableCell>msg_save_yn</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentTemplates.length ? (
                  currentTemplates.map((template, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Radio
                          size="small"
                          checked={selectedTemplate.msg_no === template.msg_no}
                          onChange={() => {
                            handleTemplateChange(template);
                          }
                        }
                        />
                      </TableCell>
                      <TableCell>{template.msg_no}</TableCell>
                      <TableCell>{template.msg_desc}</TableCell>
                      <TableCell>{template.msg_save_yn}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                    >
                      이용 가능한 콘텐츠가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 7, 10]}
              count={filteredTemplates.length}
              page={currentPage}
              onPageChange={handlePageChange}
              rowsPerPage={itemsPerPage}
              onRowsPerPageChange={handleItemsPerPageChange}
            />
          </TableContainer>
          <br />
          <Divider />
          <br />

          {/* Content Buttons Section */}
          <StyledBox>
            <StyledTypo>콘텐츠 버튼</StyledTypo>
          </StyledBox>
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%" }} size="small">
              <TableHead>
                <TableRow>
                  {selectedTemplate && selectedTemplate.templateMessageType === "FC" && (
                    <TableCell>캐로샐순서</TableCell>
                  )}
                  <TableCell>버튼명</TableCell>
                  <TableCell>버튼 URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedTemplate ? (
                  noContentFiles && noContentFiles.length > 0 ? 
                  (
                    renderNoContentFiles()
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={
                          selectedTemplate.templateMessageType === "FC" ? 3 : 2
                        }
                        align="center"
                      >
                        이용 가능한 콘텐츠가 없습니다.
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        selectedTemplate && selectedTemplate.templateMessageType === "FC" ? 3 : 2
                      }
                      align="center"
                    >
                      콘텐츠를 먼저 선택해주세요
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <br />
          {/* Content Image Section */}
          <StyledBox component="div">
            <StyledTypo>콘텐츠 이미지</StyledTypo>
          </StyledBox>
          <TableContainer component={Paper}>
            <Table
              sx={{ width: "100%" }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>이름</TableCell>
                  <TableCell>URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedTemplate && Object.keys(selectedTemplate).length > 0 ? (
                  <TableRow>
                    <TableCell
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedTemplate?.msg_img_url}
                    </TableCell>
                    <TableCell
                      style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedTemplate && (
                        <div>
                          <a
                            target="_blank"
                            href={`${selectedTemplate?.msg_img_url}`}
                            rel="noreferrer"
                          >
                            {`${selectedTemplate?.msg_img_url}`}
                          </a>
                          <br />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      콘텐츠를 먼저 선택해주세요
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <br />
          {/* ThumbNail URL Section */}
          <StyledBox>
            <StyledTypo>썸네일 URL</StyledTypo>
          </StyledBox>
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%" }} size="small">
              <TableBody>
                {selectedTemplate 
                  ? 
                  (
                  selectedTemplate.msg_img_link_url ? 
                  (
                    <TableRow>
                      <TableCell
                        colSpan={
                          2
                        }
                        align="center"
                      >
                        {selectedTemplate.msg_img_link_url}
                      </TableCell>
                    </TableRow>
                    
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={
                          selectedTemplate.templateMessageType === "FC" ? 3 : 2
                        }
                        align="center"
                      >
                        이용 가능한 콘텐츠가 없습니다.
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        selectedTemplate && selectedTemplate.templateMessageType === "FC" ? 3 : 2
                      }
                      align="center"
                    >
                      콘텐츠를 먼저 선택해주세요
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Variable Section */}
        </>
      )}
    </StyledPaper>
  );
}

export default Nav;
